import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  CreditCard, 
  Download, 
  Calendar,
  MapPin,
  Users,
  MessageSquare,
  Mail,
  CheckCircle,
  AlertCircle,
  ArrowUpRight,
  Loader2,
  XCircle,
  Banknote,
} from "lucide-react";
import { useBusiness } from "@/hooks/use-business";
import { useBilling } from "@/hooks/use-billing";
import { useSubscription } from "@/hooks/use-subscription";
import { useToast } from "@/hooks/use-toast";
import { useTrial } from "@/hooks/use-trial";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { formatPlanPriceDisplay } from "@/lib/fx-rates";
import { buildPaystackCallbackUrl } from "@/lib/payments";
import { isNativeIosApp } from "@/lib/platform";
import { isPerBookingBilling, isPerBookingActivated, isPerBookingSignupLocked, needsPerBookingSignupFee, perBookingGraceDaysRemaining, perBookingGraceEndsAt } from "@/lib/billing-model";
import { formatActivationFeeForPlan, getActivationFeeGhs, normalizeActivationPlanId } from "@/lib/signup-fee";

// Currency helper
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    GHS: "₵",
    NGN: "₦",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatCurrency = (amount: number, currency: string = "GHS"): string => {
  return `${getCurrencySymbol(currency)}${amount.toFixed(2)}`;
};

function UsageCard({ 
  icon: Icon, 
  label, 
  used, 
  limit,
}: { 
  icon: React.ElementType; 
  label: string; 
  used: number; 
  limit: number | null;
}) {
  const percentage = limit ? (used / limit) * 100 : 0;
  const isNearLimit = limit ? percentage >= 80 : false;
  const isUnlimited = limit === null;
  
  return (
    <div className="glass-card p-4 sm:p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate">{label}</p>
          <p className="text-xs text-muted-foreground">This billing cycle</p>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-bold text-foreground">{used}</span>
          <span className="text-sm text-muted-foreground">
            {isUnlimited ? "Unlimited" : `/ ${limit}`}
          </span>
        </div>
        {!isUnlimited && (
          <>
        <Progress value={percentage} className={isNearLimit ? "[&>div]:bg-warning" : ""} />
        {isNearLimit && (
          <p className="text-xs text-warning flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Approaching limit
          </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { business, loading: businessLoading } = useBusiness();
  const { metrics, loading: billingLoading, error } = useBilling();
  const isUsagePage =
    location.pathname === "/app/usage" || location.pathname.startsWith("/app/usage/");
  const initialUsageTab = (() => {
    const fromState = String((location.state as { tab?: string } | null)?.tab || "");
    if (fromState === "invoices" || fromState === "payments" || fromState === "usage") return fromState;
    return "usage";
  })();
  const [selectedTab, setSelectedTab] = useState(isUsagePage ? initialUsageTab : "overview");
  const { plans, currentPlan, loading: plansLoading, switchPlan, isUpdatingPlan } = useSubscription();
  const { toast } = useToast();
  const { user } = useAuth();
  const { isTrial } = useTrial();
  const [startingCheckoutPlanId, setStartingCheckoutPlanId] = useState<string | null>(null);
  const [payingSignupFee, setPayingSignupFee] = useState(false);
  const [activationPlanId, setActivationPlanId] = useState("starter");
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">("monthly");
  const isNativeIos = isNativeIosApp();

  useEffect(() => {
    if (business?.id) {
      setActivationPlanId(normalizeActivationPlanId((business as any)?.plan_id));
    }
  }, [business?.id, (business as any)?.plan_id]);

  useEffect(() => {
    // Apple: keep payment/subscription surfaces off iOS. Usage metrics stay available.
    if (isNativeIos && !isUsagePage) {
      navigate("/app/usage", { replace: true });
    }
  }, [isNativeIos, isUsagePage, navigate]);

  useEffect(() => {
    if (isUsagePage) {
      const fromState = String((location.state as { tab?: string } | null)?.tab || "");
      setSelectedTab(
        fromState === "invoices" || fromState === "payments" || fromState === "usage"
          ? fromState
          : "usage",
      );
      return;
    }
    setSelectedTab("overview");
  }, [isUsagePage, location.state]);

  const isLoading = businessLoading || billingLoading;
  const primaryModule = String((business as any)?.primary_module || "appointments");
  const isCateringServices = primaryModule === "catering_services" || primaryModule === "food_vendors";
  const bookingUnitLabel = isCateringServices ? "Orders" : "Appointments";

  const displayCurrency = business?.currency || "GHS";

  if (isNativeIos && !isUsagePage) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  const formatCurrencyValue = (amount: number) => {
    return formatCurrency(amount, displayCurrency);
  };
  const perBooking = isPerBookingBilling(business);
  const perBookingActivated = perBooking && isPerBookingActivated(business);
  const perBookingLocked = perBooking && isPerBookingSignupLocked(business);
  const perBookingNeedsFee = perBooking && needsPerBookingSignupFee(business);
  const perBookingGraceDays = perBookingNeedsFee ? perBookingGraceDaysRemaining(business) : 0;
  const perBookingGraceEndsLabel =
    perBooking && perBookingGraceEndsAt(business)
      ? format(new Date(perBookingGraceEndsAt(business)!), "MMM d, yyyy")
      : null;
  const activationFeeLabel = formatActivationFeeForPlan(activationPlanId);
  const activationPlanOptions = [
    { id: "starter", name: "Starter", feeGhs: getActivationFeeGhs("starter") },
    { id: "professional", name: "Pro", feeGhs: getActivationFeeGhs("professional") },
  ] as const;
  const activationPlanPicker =
    perBooking && !perBookingActivated ? (
      <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
        {activationPlanOptions.map((opt) => {
          const selected = activationPlanId === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setActivationPlanId(opt.id)}
              className={cn(
                "text-left rounded-xl border p-4 transition-colors",
                selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "border-border hover:border-primary/40",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{opt.name}</span>
                {selected && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                One-time payment {formatActivationFeeForPlan(opt.id)}
              </p>
            </button>
          );
        })}
      </div>
    ) : null;

  /** Plan prices are stored in GHS; convert to business currency and round up to whole number */
  const formatPlanPrice = (amountGhs: number) => {
    return formatPlanPriceDisplay(amountGhs, displayCurrency);
  };

  const generateReceipt = (invoiceData: {
    paymentId: string;
    reference: string;
    date: string;
    amount: number;
    currency: string;
    payer: string;
    payerEmail: string;
    type: string;
    fee: number;
    net: number;
  }) => {
    // Create a simple HTML receipt
    const receiptHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt - ${invoiceData.reference}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto; }
    .header { text-align: center; margin-bottom: 30px; }
    .business-name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
    .receipt-title { font-size: 18px; color: #666; }
    .details { margin: 30px 0; }
    .detail-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
    .detail-label { font-weight: 600; }
    .total { margin-top: 20px; padding-top: 20px; border-top: 2px solid #000; }
    .total-row { display: flex; justify-content: space-between; font-size: 18px; font-weight: bold; padding: 10px 0; }
    .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="business-name">${business?.name || "Buukd"}</div>
    <div class="receipt-title">Payment Receipt</div>
  </div>
  <div class="details">
    <div class="detail-row">
      <span class="detail-label">Receipt Number:</span>
      <span>${invoiceData.reference}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Date:</span>
      <span>${invoiceData.date}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Payment Type:</span>
      <span>${invoiceData.type}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Payer:</span>
      <span>${invoiceData.payer}</span>
    </div>
    ${invoiceData.payerEmail ? `<div class="detail-row"><span class="detail-label">Email:</span><span>${invoiceData.payerEmail}</span></div>` : ""}
  </div>
  <div class="total">
    <div class="detail-row">
      <span>Amount:</span>
      <span>${formatCurrency(invoiceData.amount, invoiceData.currency)}</span>
    </div>
    ${invoiceData.fee > 0 ? `<div class="detail-row"><span>Fee:</span><span>${formatCurrency(invoiceData.fee, invoiceData.currency)}</span></div>` : ""}
    ${invoiceData.fee > 0 ? `<div class="detail-row"><span>Net Amount:</span><span>${formatCurrency(invoiceData.net, invoiceData.currency)}</span></div>` : ""}
    <div class="total-row">
      <span>Total Paid:</span>
      <span>${formatCurrency(invoiceData.amount, invoiceData.currency)}</span>
    </div>
  </div>
  <div class="footer">
    <p>Thank you for your payment!</p>
    <p>This is an automated receipt. For questions, please contact support.</p>
  </div>
</body>
</html>`;

    // Open in new window for printing
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(receiptHTML);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  // Calculate next billing date: use subscription_next_payment_at if set,
  // otherwise calculate from subscription_last_paid_at + 1 month
  const nextBillingDate = (business as any)?.subscription_next_payment_at
    ? format(new Date((business as any).subscription_next_payment_at), "MMM d, yyyy")
    : (business as any)?.subscription_last_paid_at
    ? (() => {
        const lastPaid = new Date((business as any).subscription_last_paid_at);
        const nextDate = new Date(lastPaid);
        nextDate.setMonth(nextDate.getMonth() + 1);
        return format(nextDate, "MMM d, yyyy");
      })()
    : "Not set";

  const handlePaySignupFee = async () => {
    if (!business?.id) return;
    if (isNativeIos || isUsagePage) {
      toast({
        title: "One-time payment",
        description: "Please contact Buukd support to pay your one-time payment.",
      });
      return;
    }
    const payerEmail = (user?.email || "").trim();
    if (!payerEmail) {
      toast({ title: "Missing email", description: "Please re-authenticate and try again.", variant: "destructive" });
      return;
    }

    setPayingSignupFee(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session) {
        toast({
          title: "Authentication error",
          description: "Please log out and log back in to continue.",
          variant: "destructive",
        });
        return;
      }

      const callbackUrl = buildPaystackCallbackUrl("/app/billing");
      const { data, error } = await supabase.functions.invoke("paystack-init", {
        body: {
          context: {
            type: "platform_signup_fee",
            businessId: business.id,
            planId: normalizeActivationPlanId(activationPlanId),
          },
          payerEmail,
          payerName: String(user?.user_metadata?.name || "").trim() || null,
          callbackUrl,
        },
      });
      if (error) throw error;
      const url = String((data as any)?.authorizationUrl || "");
      if (!url) throw new Error("Missing Paystack authorizationUrl");

      setTimeout(() => {
        window.location.assign(url);
      }, 50);
    } catch (e: any) {
      toast({ title: "Failed to start checkout", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setPayingSignupFee(false);
    }
  };

  const handleResubscribe = async () => {
    if (!business?.id) return;
    
    // Use current plan_id or default to starter
    const planId = (business as any)?.plan_id || "starter";
    await handleSwitchPlan(planId, "monthly");
  };

  const handleSwitchPlan = async (planId: string, billingInterval: "monthly" | "yearly" = "monthly") => {
    const target = plans.find((p) => p.id === planId);
    if (!target) return;
    if (!business?.id) return;
    if (isNativeIos || isUsagePage) {
      toast({
        title: "Subscription management",
        description: "Subscription changes are handled outside the iOS app by the organization administrator.",
      });
      return;
    }
    const payerEmail = (user?.email || "").trim();
    if (!payerEmail) {
      toast({ title: "Missing email", description: "Please re-authenticate and try again.", variant: "destructive" });
      return;
    }

    setStartingCheckoutPlanId(planId);
    try {
      // Get current session to ensure we have a valid token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session) {
        toast({
          title: "Authentication error",
          description: "Please log out and log back in to continue.",
          variant: "destructive",
        });
        return;
      }

      const callbackUrl = buildPaystackCallbackUrl("/app/billing");
      const { data, error } = await supabase.functions.invoke("paystack-init", {
        body: {
          context: { type: "platform_subscription_setup", businessId: business.id, planId, billingInterval },
          payerEmail,
          payerName: String(user?.user_metadata?.name || "").trim() || null,
          callbackUrl,
        },
      });
      if (error) throw error;
      const url = String((data as any)?.authorizationUrl || "");
      if (!url) throw new Error("Missing Paystack authorizationUrl");
      
      // Safari mobile fix: Use assign() with a small delay to prevent blocking
      // Safari can block immediate redirects after async operations
      setTimeout(() => {
        window.location.assign(url);
      }, 50);
    } catch (e: any) {
      toast({ title: "Failed to start checkout", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setStartingCheckoutPlanId(null);
    }
  };

  if (isLoading) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader title="Billing" description="Manage your subscription and view usage" />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  if (error) {
  return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader
          title="Billing"
          description={
            perBooking ? "Your Buukd account and online payment activity" : "Manage your subscription and view usage"
          }
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
          <XCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-destructive">{error}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title={isUsagePage ? "Usage" : "Billing"}
        description={
          isUsagePage
            ? "Track bookings, staff, locations, customers, and campaign usage"
            : perBooking
              ? "Your Buukd account and online payment activity"
              : "Manage your subscription and plan"
        }
      />

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4 sm:space-y-6">
        {isUsagePage ? (
          <TabsList className="glass-card grid w-full h-auto grid-cols-3 gap-1 p-1">
            <TabsTrigger value="usage" className="w-full text-xs sm:text-sm">Usage</TabsTrigger>
            <TabsTrigger value="invoices" className="w-full text-xs sm:text-sm">Invoices</TabsTrigger>
            <TabsTrigger value="payments" className="w-full text-xs sm:text-sm">Payments</TabsTrigger>
          </TabsList>
        ) : selectedTab === "plans" && !perBooking ? (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs sm:text-sm -ml-2"
              onClick={() => setSelectedTab("overview")}
            >
              ← Back to overview
            </Button>
          </div>
        ) : null}

        {/* Billing page: overview only (activation / subscription / revenue summary) */}
        {!isUsagePage && (
        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          {perBooking ? (
            <div className="glass-card p-4 sm:p-6 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-semibold text-foreground">Pay as you grow</h3>
                <Badge
                  className={
                    perBookingActivated
                      ? "bg-success/10 text-success border-success/20"
                      : perBookingLocked
                        ? "bg-destructive/10 text-destructive border-destructive/20"
                        : "bg-blue-500/10 text-blue-500 border-blue-500/20"
                  }
                >
                  {perBookingActivated ? "Active" : perBookingLocked ? "Locked" : "Setup period"}
                </Badge>
              </div>

              {perBookingActivated ? (
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Your account has been activated. Add your MoMo or bank payout details in Settings → Payments & Payouts.
                </p>
              ) : perBookingLocked ? (
                <>
                  <p className="text-sm text-muted-foreground max-w-2xl">
                    Your 7-day setup period has ended. Pay the one-time payment ({activationFeeLabel}) to unlock your
                    account.
                  </p>
                  {activationPlanPicker}
                  <Button onClick={handlePaySignupFee} disabled={payingSignupFee} className="gap-2">
                    {payingSignupFee ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Pay {activationFeeLabel}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground max-w-2xl">
                    New accounts get <strong className="font-medium text-foreground">7 days</strong> to explore Buukd
                    before a one-time payment is required. Choose Starter ({formatActivationFeeForPlan("starter")}) or
                    Pro ({formatActivationFeeForPlan("professional")}). Pay anytime during this period to stay active
                    with no monthly subscription.
                  </p>
                  {activationPlanPicker}
                  {perBookingGraceEndsLabel && (
                    <p className="text-sm text-foreground">
                      Setup period ends {perBookingGraceEndsLabel}
                      {perBookingGraceDays > 0
                        ? ` (${perBookingGraceDays} ${perBookingGraceDays === 1 ? "day" : "days"} left)`
                        : " (today)"}
                    </p>
                  )}
                  <Button onClick={handlePaySignupFee} disabled={payingSignupFee} variant="default" className="gap-2">
                    {payingSignupFee ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Pay now ({activationFeeLabel})
                  </Button>
                </>
              )}
            </div>
          ) : (
          <>
          <div className="glass-card p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground">
                    {isNativeIos ? "Organization Access" : isTrial ? "Free Trial" : (currentPlan?.name || "Starter") + " Plan"}
                  </h3>
                  <Badge
                    variant="secondary"
                    className={
                      (business as any)?.subscription_status === "active"
                        ? "bg-success/10 text-success border-success/20"
                        : isTrial
                        ? "bg-blue-500/10 text-blue-500 border-blue-500/20"
                        : "bg-muted text-muted-foreground"
                    }
                  >
                    {(business as any)?.subscription_status === "active"
                      ? "Active"
                      : isTrial
                      ? "Trial"
                      : "Inactive"}
                  </Badge>
                </div>
                {isNativeIos ? (
                  <p className="text-sm text-muted-foreground">
                    Subscription and billing are managed by your organization administrator.
                  </p>
                ) : isTrial ? (
                  <p className="text-muted-foreground">
                    <span className="text-sm">Select a plan below to subscribe</span>
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    <span className="text-xl sm:text-2xl font-bold text-foreground">
                      {currentPlan?.price_ghs != null ? formatPlanPrice(Number(currentPlan.price_ghs)) : "Custom"}
                    </span>
                    <span className="text-sm">/monthly</span>
                  </p>
                )}
              </div>
              {!isNativeIos && (
              <div className="flex gap-2 sm:gap-3">
                {(business as any)?.subscription_status === "grace_period" || 
                 (business as any)?.subscription_status === "suspended" ? (
                  <Button
                    size="sm"
                    className="text-xs sm:text-sm bg-red-600 hover:bg-red-700"
                    onClick={handleResubscribe}
                    disabled={isNativeIos || isUpdatingPlan || startingCheckoutPlanId !== null}
                  >
                    {isNativeIos ? (
                      "Managed by organization"
                    ) : isUpdatingPlan || startingCheckoutPlanId !== null ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Resubscribe Now"
                    )}
                  </Button>
                ) : isTrial ? (
                  <Button
                    size="sm"
                    className="text-xs sm:text-sm bg-primary hover:bg-primary/90"
                    onClick={() => setSelectedTab("plans")}
                    disabled={isNativeIos}
                  >
                    {isNativeIos ? "Managed by organization" : "Subscribe Now"}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" className="text-xs sm:text-sm" disabled={isNativeIos}>
                      Cancel Plan
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => setSelectedTab("plans")}
                      disabled={isNativeIos}
                    >
                      Change Plan
                    </Button>
                  </>
                )}
              </div>
              )}
            </div>
            {isNativeIos ? (
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border">
                <p className="text-xs sm:text-sm text-muted-foreground">Subscription management</p>
                <p className="mt-1 text-sm font-medium text-foreground">
                  Managed by organization
                </p>
                <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                  Your organization administrator manages subscription access outside the iOS app.
                </p>
              </div>
            ) : (
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Next billing date</p>
                  <p className="font-medium text-foreground text-sm sm:text-base">
                    {nextBillingDate}
                  </p>
                  {(business as any)?.subscription_status === "active" && !(business as any)?.subscription_next_payment_at && (business as any)?.subscription_last_paid_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      If you were charged recently, renewal tracking may still be updating. Check the Payments tab for subscription entries.
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Payment method</p>
                  <p className="font-medium text-foreground flex items-center gap-2 text-sm sm:text-base">
                    <CreditCard className="h-4 w-4" />
                    Manual / Not set
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Billing email</p>
                  <p className="font-medium text-foreground text-sm sm:text-base truncate">
                    {user?.email || "—"}
                  </p>
                </div>
              </div>
            )}
          </div>
          </>
          )}

          {/* Revenue Summary */}
          <div className="glass-card p-4 sm:p-6">
            <h3 className="font-semibold text-foreground mb-1 text-base sm:text-lg">
              Revenue Summary
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Money from your customers (deposits, invoices, memberships, etc.).
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">This Month</p>
                <p className="text-lg sm:text-2xl font-bold text-foreground">
                  {formatCurrencyValue(metrics.revenueThisMonth)}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-lg sm:text-2xl font-bold text-foreground">
                  {formatCurrencyValue(metrics.totalRevenue)}
                </p>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className="text-xs sm:text-sm text-muted-foreground">Total Paid Payments</p>
                <p className="text-lg sm:text-2xl font-bold text-foreground">
                  {metrics.paidPaymentsCount}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Usage Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <UsageCard 
              icon={Calendar} 
              label={bookingUnitLabel}
              used={metrics.appointmentsThisMonth}
              limit={metrics.appointmentLimit}
            />
            <UsageCard 
              icon={MessageSquare} 
              label="SMS Campaigns"
              used={metrics.smsCampaignsThisMonth}
              limit={metrics.smsCampaignLimit}
            />
            <UsageCard 
              icon={Mail} 
              label="Email Campaigns"
              used={metrics.emailCampaignsThisMonth}
              limit={metrics.emailCampaignLimit}
            />
            <UsageCard icon={Users} label="Staff Members" used={metrics.staffCount} limit={metrics.staffLimit} />
          </div>

          {/* Recent Invoices */}
          <div className="glass-card p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground text-base sm:text-lg">Recent Invoices</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/app/usage", { state: { tab: "invoices" } })}
                className="text-xs sm:text-sm"
              >
                View all
                <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
              </Button>
            </div>
            {metrics.invoices.length > 0 ? (
            <div className="divide-y divide-border">
                {metrics.invoices.slice(0, 3).map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between py-3 flex-wrap gap-2"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-foreground text-sm sm:text-base truncate">
                          {invoice.id}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">{invoice.date}</p>
                    </div>
                  </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <span className="font-medium text-foreground text-sm sm:text-base">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </span>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <p>No invoices yet</p>
              </div>
            )}
          </div>
        </TabsContent>
        )}

        {/* Usage page: usage / invoices / payments */}
        {isUsagePage && (
        <>
        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-4 sm:space-y-6">
          <div className="glass-card p-4 sm:p-6">
            <h3 className="font-semibold text-foreground mb-4 sm:mb-6 text-base sm:text-lg">
              Current Billing Cycle Usage
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="p-4 rounded-xl border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm sm:text-base">{bookingUnitLabel}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {metrics.appointmentsThisMonth} of{" "}
                      {metrics.appointmentLimit === null ? "unlimited" : metrics.appointmentLimit} used
                    </p>
                  </div>
                </div>
                {metrics.appointmentLimit !== null && (
                  <>
                    <Progress
                      value={
                        metrics.appointmentLimit
                          ? (metrics.appointmentsThisMonth / metrics.appointmentLimit) * 100
                          : 0
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.max(0, metrics.appointmentLimit - metrics.appointmentsThisMonth)} remaining
                      this cycle
                    </p>
                  </>
                )}
              </div>

              <div className="p-4 rounded-xl border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm sm:text-base">SMS Campaigns</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {metrics.smsCampaignsThisMonth} of{" "}
                      {metrics.smsCampaignLimit === null ? "unlimited" : metrics.smsCampaignLimit} used
                    </p>
                  </div>
                </div>
                {metrics.smsCampaignLimit !== null && (
                  <>
                    <Progress
                      value={
                        metrics.smsCampaignLimit
                          ? (metrics.smsCampaignsThisMonth / metrics.smsCampaignLimit) * 100
                          : 0
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.max(0, metrics.smsCampaignLimit - metrics.smsCampaignsThisMonth)} remaining this cycle
                    </p>
                  </>
                )}
              </div>

              <div className="p-4 rounded-xl border border-border">
                    <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-primary" />
                      </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm sm:text-base">Email Campaigns</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {metrics.emailCampaignsThisMonth} of{" "}
                      {metrics.emailCampaignLimit === null ? "unlimited" : metrics.emailCampaignLimit} used
                        </p>
                      </div>
                    </div>
                {metrics.emailCampaignLimit !== null && (
                  <>
                    <Progress
                      value={
                        metrics.emailCampaignLimit
                          ? (metrics.emailCampaignsThisMonth / metrics.emailCampaignLimit) * 100
                          : 0
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.max(0, metrics.emailCampaignLimit - metrics.emailCampaignsThisMonth)} remaining this cycle
                    </p>
                  </>
                )}
              </div>

              <div className="p-4 rounded-xl border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm sm:text-base">Staff Members</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {metrics.staffCount} of{" "}
                      {metrics.staffLimit === null ? "unlimited" : metrics.staffLimit} used
                    </p>
                  </div>
                </div>
                {metrics.staffLimit !== null && (
                  <>
                    <Progress
                      value={metrics.staffLimit ? (metrics.staffCount / metrics.staffLimit) * 100 : 0}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.max(0, metrics.staffLimit - metrics.staffCount)} remaining this cycle
                    </p>
                  </>
                )}
              </div>

              <div className="p-4 rounded-xl border border-border">
                    <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                      </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm sm:text-base">Locations</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {metrics.locationsCount} of{" "}
                      {metrics.locationLimit === null ? "unlimited" : metrics.locationLimit} used
                        </p>
                      </div>
                    </div>
                {metrics.locationLimit !== null && (
                  <>
                    <Progress
                      value={
                        metrics.locationLimit
                          ? (metrics.locationsCount / metrics.locationLimit) * 100
                          : 0
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.max(0, metrics.locationLimit - metrics.locationsCount)} remaining this
                      cycle
                    </p>
                  </>
                )}
              </div>

              <div className="p-4 rounded-xl border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm sm:text-base">Customers</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {metrics.customersCount} of{" "}
                      {metrics.customerLimit === null ? "unlimited" : metrics.customerLimit} used
                    </p>
                  </div>
                </div>
                {metrics.customerLimit !== null && (
                  <>
                    <Progress
                      value={
                        metrics.customerLimit
                          ? (metrics.customersCount / metrics.customerLimit) * 100
                          : 0
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.max(0, metrics.customerLimit - metrics.customersCount)} remaining (cumulative)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="glass-card p-4 sm:p-6">
            <h3 className="font-semibold text-foreground mb-4 text-base sm:text-lg">Usage Tips</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-accent">
                <MessageSquare className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">Optimize SMS Usage</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Use email reminders for non-urgent communications to save SMS credits
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-accent">
                <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">{isCateringServices ? "Improve Delivery Updates" : "Reduce No-Shows"}</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    {isCateringServices
                      ? "Keep customers updated with order status changes to reduce failed deliveries."
                      : "Enable appointment reminders to reduce cancellations and maximize bookings"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4 sm:space-y-6">
          <div className="glass-card overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border">
              <h3 className="font-semibold text-foreground text-base sm:text-lg">Recent Invoice History</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Showing the latest payment receipts. Revenue totals include all successful payments.
              </p>
            </div>
            {metrics.invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Invoice ID</TableHead>
                      <TableHead className="text-xs sm:text-sm">Date</TableHead>
                      <TableHead className="text-xs sm:text-sm">Amount</TableHead>
                      <TableHead className="text-xs sm:text-sm">Status</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">{invoice.id}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{invoice.date}</TableCell>
                        <TableCell className="text-xs sm:text-sm font-semibold">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className="bg-success/10 text-success border-success/20 text-xs"
                          >
                      Paid
                    </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                              // Find the payment for this invoice
                              const payment = metrics.payments.find((p) => p.id === invoice.paymentId);
                              if (payment) {
                                const payerName = payment.payer_name || payment.customer?.name || payment.payer_email || "—";
                                const displayDate = payment.paid_at ? new Date(payment.paid_at) : new Date(payment.created_at);
                                const getPaymentTypeLabel = (context: string, meta?: { renewal?: boolean } | null) => {
                                  const labels: Record<string, string> = {
                                    platform_subscription_setup: meta?.renewal ? "Subscription (renewal)" : "Subscription",
                                    appointment_deposit: "Appointment Deposit",
                                    invoice_payment: "Invoice Payment",
                                    membership_payment: "Membership Payment",
                                    day_pass_payment: "Day Pass",
                                  };
                                  return labels[context] || context.replace(/_/g, " ");
                                };
                                generateReceipt({
                                  paymentId: payment.id,
                                  reference: invoice.reference,
                                  date: invoice.date,
                                  amount: invoice.amount,
                                  currency: invoice.currency,
                                  payer: payerName,
                                  payerEmail: payment.payer_email,
                                  type: getPaymentTypeLabel(payment.context, payment.context_meta),
                                  fee: parseFloat(String(payment.fee_amount || 0)),
                                  net: parseFloat(String(payment.net_amount || 0)),
                                });
                              }
                            }}
                          >
                            <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">PDF</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">No invoices found</p>
              </div>
            )}
                  </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4 sm:space-y-6">
          <div className="glass-card overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border">
              <h3 className="font-semibold text-foreground text-base sm:text-lg">Recent Payment History</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Showing recent payments only.
              </p>
            </div>
            {metrics.payments.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Date</TableHead>
                      <TableHead className="text-xs sm:text-sm">Type</TableHead>
                      <TableHead className="text-xs sm:text-sm">Payer</TableHead>
                      <TableHead className="text-xs sm:text-sm">Amount</TableHead>
                      <TableHead className="text-xs sm:text-sm">Status</TableHead>
                      <TableHead className="text-xs sm:text-sm">Reference</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.payments.map((payment) => {
                      const getPaymentTypeLabel = (context: string, meta?: { renewal?: boolean } | null) => {
                        const labels: Record<string, string> = {
                          platform_subscription_setup: meta?.renewal ? "Subscription (renewal)" : "Subscription",
                          appointment_deposit: "Appointment Deposit",
                          invoice_payment: "Invoice Payment",
                          membership_payment: "Membership Payment",
                          day_pass_payment: "Day Pass",
                        };
                        return labels[context] || context.replace(/_/g, " ");
                      };

                      const getStatusBadge = (status: string) => {
                        const statusConfig: Record<string, { className: string; label: string }> = {
                          success: {
                            className: "bg-success/10 text-success border-success/20",
                            label: "Success",
                          },
                          pending: {
                            className: "bg-warning/10 text-warning border-warning/20",
                            label: "Pending",
                          },
                          failed: {
                            className: "bg-destructive/10 text-destructive border-destructive/20",
                            label: "Failed",
                          },
                          abandoned: {
                            className: "bg-muted/10 text-muted-foreground border-muted/20",
                            label: "Abandoned",
                          },
                          refunded: {
                            className: "bg-orange-500/10 text-orange-500 border-orange-500/20",
                            label: "Refunded",
                          },
                        };
                        const config = statusConfig[status] || {
                          className: "bg-muted/10 text-muted-foreground border-muted/20",
                          label: status,
                        };
                        return config;
                      };

                      const statusConfig = getStatusBadge(payment.status);
                      const payerName = payment.payer_name || payment.customer?.name || payment.payer_email || "—";
                      const displayDate = payment.paid_at ? new Date(payment.paid_at) : new Date(payment.created_at);

                      return (
                        <TableRow key={payment.id}>
                          <TableCell className="text-xs sm:text-sm">
                            {format(displayDate, "MMM d, yyyy HH:mm")}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm capitalize">
                            {getPaymentTypeLabel(payment.context, payment.context_meta)}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            <div>
                              <div>{payerName}</div>
                              {payment.payer_email && payerName !== payment.payer_email && (
                                <div className="text-muted-foreground text-xs">{payment.payer_email}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm font-semibold">
                            {formatCurrency(
                              parseFloat(String(payment.gross_amount || 0)),
                              payment.currency || business?.currency || "GHS"
                            )}
                            {payment.fee_amount > 0 && (
                              <div className="text-muted-foreground text-xs font-normal">
                                Fee: {formatCurrency(parseFloat(String(payment.fee_amount)), payment.currency || "GHS")}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={statusConfig.className} style={{ fontSize: "0.7rem" }}>
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm font-mono">
                            {payment.provider_reference || payment.id.slice(0, 8)}
                          </TableCell>
                          <TableCell className="text-right">
                            {payment.status === "success" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={() => {
                                  // Generate invoice/receipt
                                  const invoiceData = {
                                    paymentId: payment.id,
                                    reference: payment.provider_reference || payment.id,
                                    date: format(displayDate, "yyyy-MM-dd"),
                                    amount: parseFloat(String(payment.gross_amount || 0)),
                                    currency: payment.currency || "GHS",
                                    payer: payerName,
                                    payerEmail: payment.payer_email,
                                    type: getPaymentTypeLabel(payment.context, payment.context_meta),
                                    fee: parseFloat(String(payment.fee_amount || 0)),
                                    net: parseFloat(String(payment.net_amount || 0)),
                                  };
                                  generateReceipt(invoiceData);
                                }}
                              >
                                <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">Receipt</span>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">No payments found</p>
              </div>
            )}
          </div>
        </TabsContent>
        </>
        )}

        {/* Plans — subscription businesses only, reachable from Billing overview CTAs */}
        {!isUsagePage && !isNativeIos && !perBooking && (
        <TabsContent value="plans" className="space-y-4 sm:space-y-6">
          {/* Billing interval toggle */}
          <div className="flex justify-center sm:justify-end">
            <div className="inline-flex items-center rounded-full bg-background border border-border shadow-soft p-1">
              <button
                type="button"
                onClick={() => setBillingInterval("monthly")}
                className={cn(
                  "px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-full transition-colors",
                  billingInterval === "monthly"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingInterval("yearly")}
                className={cn(
                  "px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1",
                  billingInterval === "yearly"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Yearly
                <span className="hidden sm:inline text-[11px] font-semibold">
                  <span className="inline-flex items-center rounded-full border border-emerald-500/60 bg-emerald-100 px-2 py-0.5 text-emerald-700">
                    Save 20%
                  </span>
                </span>
              </button>
            </div>
          </div>

          {isNativeIos && (
            <div className="glass-card p-4 sm:p-6 text-sm text-muted-foreground">
              Subscription purchases and plan changes are managed outside the iOS app by the organization administrator.
              Existing subscriptions and usage remain visible here.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {(plansLoading ? [] : plans).map((plan) => {
              // Only show as current if subscription is active, not during trial
              const isCurrent = !isTrial && currentPlan?.id === plan.id;
              const features = [
                plan.booking_limit_monthly == null
                  ? isCateringServices
                    ? "Custom order volume"
                    : "Custom booking volume"
                  : `${plan.booking_limit_monthly.toLocaleString()} ${isCateringServices ? "orders" : "bookings"} / month`,
                plan.staff_limit == null ? "Custom staff limits" : `Up to ${plan.staff_limit} staff`,
                plan.location_limit == null ? "Custom locations" : `${plan.location_limit} business location${plan.location_limit === 1 ? "" : "s"}`,
                plan.sms_campaign_limit_monthly == null ? "Custom SMS campaigns" : `${plan.sms_campaign_limit_monthly.toLocaleString()} SMS campaigns / month`,
                plan.email_campaign_limit_monthly == null ? "Custom Email campaigns" : `${plan.email_campaign_limit_monthly.toLocaleString()} Email campaigns / month`,
                plan.customer_limit_cumulative == null ? "Custom customer limits" : `${plan.customer_limit_cumulative.toLocaleString()} customers (cumulative)`,
              ];
              // Prefer yearly price from DB; fall back to known marketing numbers so UI stays in sync
              let yearlyPrice = plan.price_yearly_ghs != null ? Number(plan.price_yearly_ghs) : null;
              if (!yearlyPrice || yearlyPrice <= 0) {
                if (plan.id === "starter") yearlyPrice = 1429;
                else if (plan.id === "professional") yearlyPrice = 3490;
                else if (plan.id === "enterprise") yearlyPrice = 9599;
              }
              const hasYearly = !!yearlyPrice && yearlyPrice > 0;
              const effectiveInterval: "monthly" | "yearly" =
                billingInterval === "yearly" && hasYearly ? "yearly" : "monthly";
              const displayAmount =
                effectiveInterval === "yearly" && yearlyPrice ? yearlyPrice : Number(plan.price_ghs || 0);
              const isCurrentInterval = isCurrent && effectiveInterval === "monthly";

              return (
                <div
                  key={plan.id}
                  className={`glass-card p-4 sm:p-6 relative ${isCurrent ? "ring-2 ring-primary" : ""}`}
              >
                  {isCurrent && (
                    <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs">
                    Current Plan
                  </Badge>
                )}
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-foreground">{plan.name}</h3>
                    <div className="mt-2">
                      <span className="text-2xl sm:text-3xl font-bold text-foreground">
                        {displayAmount ? formatPlanPrice(displayAmount) : "Custom"}
                      </span>
                      <span className="text-muted-foreground text-sm sm:text-base">
                        {effectiveInterval === "yearly" ? "/year" : "/mo"}
                      </span>
                    </div>
                    {hasYearly ? (
                      <div className="mt-1 text-[11px] sm:text-xs text-muted-foreground">
                        {effectiveInterval === "yearly" ? (
                          <>Billed once a year. Save up to 3 months.</>
                        ) : (
                          <>
                            Or{" "}
                            <span className="font-semibold text-foreground">
                              {formatPlanPrice(yearlyPrice!)}
                            </span>{" "}
                            /year (save up to 3 months)
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs sm:text-sm">
                        <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-success flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full text-xs sm:text-sm"
                  variant={isCurrentInterval ? "outline" : "default"}
                  disabled={isNativeIos || isCurrentInterval || isUpdatingPlan || startingCheckoutPlanId === plan.id}
                  onClick={() => handleSwitchPlan(plan.id, effectiveInterval)}
                >
                  {isNativeIos
                    ? "Managed by organization"
                    : isCurrentInterval
                    ? "Current Plan"
                    : startingCheckoutPlanId === plan.id
                      ? "Redirecting…"
                      : effectiveInterval === "yearly"
                        ? "Select yearly plan"
                        : "Select monthly plan"}
                </Button>
              </div>
              );
            })}
          </div>

          <div className="glass-card p-4 sm:p-6 text-center">
            <h3 className="font-semibold text-foreground mb-2 text-base sm:text-lg">Need more?</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mb-4">
              Contact us for custom enterprise plans with unlimited everything
            </p>
            <Button variant="outline" size="sm" className="text-xs sm:text-sm">
              Contact Sales
            </Button>
          </div>
        </TabsContent>
        )}
      </Tabs>
    </AppShell>
  );
}
